import { SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Action, ActionReducer, createFeature, createReducer, on } from '@ngrx/store';

import { differenceBy } from 'lodash';
import { NewOpportunityAddInventoryActions } from 'src/app/new-opportunity/new-opportunity-add-inventory/new-opportunity-add-inventory.actions';
import { NewOpportunityLocationsActions } from 'src/app/new-opportunity/new-opportunity-locations/new-opportunity-locations.actions';
import { NewOpportunitySimilarJobsActions } from 'src/app/new-opportunity/new-opportunity-similar-jobs/new-opportunity-similar-jobs.actions';
import { NewOpportunityActions } from 'src/app/new-opportunity/new-opportunity.actions';

import { LocationDetailsWizardActions } from 'src/app/shared/location-details-wizard/location-details-wizard.actions';

import { JobActivityActions } from '../job/job-activity/job-activity.actions';
import { DiscardOpportunityDialogActions } from '../job/job-create/discard-job-component/discard-job.actions';
import { JobCreateInventoryActions } from '../job/job-create/job-create-inventory/job-create-inventory.actions';
import { JobCreateActions } from '../job/job-create/job-create-state/job-create.actions';
import { JobSummaryActions } from '../job/job-create/job-create-summary/job-create-summary.actions';
import { JobFormActionButtonActions } from '../job/job-create/job-form-action-button/job-form-action-button.actions';
import { JobManuallyAddLocationActions } from '../job/job-create/job-manually-add-location/job-manually-add-location.actions';
import { InventoryActions } from '../job/job-inventory/job-inventory.actions';
import { NavigatorOutboxActions } from '../job/job-navigator/navigator-outbox/navigator-outbox.actions';
import { JobLocationsOverviewActions } from '../job/job-overview/overview-locations/overview-locations.actions';
import { OverviewTimelineActions } from '../job/job-overview/overview-timeline/overview-timeline.actions';
import { JobToolActions } from '../job/job-tool.actions';
import { UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES } from '../job/job-tool.reducer';
import { WorkOrdersActions } from '../job/job-workorders/job-workorders.actions';
import { NewOpportunityCustomerActions } from '../new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { quickJsActions } from '../quickjs/quickjs.actions';
import { generateUUID } from '../utilities/state.util';

import { ActionSource } from './action-sources';

import { loadFromStorage } from './persist-state.utils';
import { appendUnsavedChange, compactUnsavedChanges } from './unsaved-changes-compaction';
import { UnsavedChangesActions } from './unsaved-changes.actions';

export const TRACKABLE_JOB_PAGE_ACTION_TYPES = [

  // Track change timeline
  OverviewTimelineActions.timelineDatesSelected,

  // Track add location
  JobLocationsOverviewActions.addLocationLinkClicked,
  JobLocationsOverviewActions.addableLocationTypeSelected,
  JobLocationsOverviewActions.navigateToInvalidLocationSuccess,

  // Track location details wizard selections
  LocationDetailsWizardActions.optionClicked,

  // Track exit location wizard
  JobLocationsOverviewActions.exitWizardButtonClicked,

  // Track customer form changes
  NewOpportunityCustomerActions.customerAutocompleteInputChanged,
  NewOpportunityCustomerActions.fullNameInputChanged,
  NewOpportunityCustomerActions.emailInputChanged,
  NewOpportunityCustomerActions.phoneInputChanged,
  NewOpportunityCustomerActions.companyInputChanged,
  NewOpportunityCustomerActions.clearCustomerButtonClicked,
  NewOpportunityCustomerActions.referralSourceSelected,
  NewOpportunityCustomerActions.customerTypeSelected,
  NewOpportunityCustomerActions.jobOriginSelected,
  NewOpportunityCustomerActions.existingCustomerSelected,
  NewOpportunityCustomerActions.unitNumberEntered,
  NewOpportunityCustomerActions.googleAutocompleteAddressSelected,
  NewOpportunityCustomerActions.useEndLocationAsBillingLocationCheckboxClicked,
  NewOpportunityCustomerActions.addAdditionalContactButtonClicked,
  NewOpportunityCustomerActions.additionalContactFieldChanged,
  NewOpportunityCustomerActions.removeAdditionalContactButtonClicked,
  NewOpportunityCustomerActions.existingAdditionalContactSelected,

  // Track location form changes
  JobLocationsOverviewActions.unitNumberEntered,
  JobLocationsOverviewActions.googleAutocompleteInputChanged,
  JobLocationsOverviewActions.googleAutocompleteAddressSelected,
  JobLocationsOverviewActions.buildingTypeSelected,
  JobLocationsOverviewActions.sizeSelected,
  JobLocationsOverviewActions.accessSelected,
  JobLocationsOverviewActions.elevatorTypeSelected,
  JobLocationsOverviewActions.floorInputChanged,
  JobLocationsOverviewActions.accessNotesTextAreaContentChanged,
  JobManuallyAddLocationActions.addLocationButtonClicked,
  NewOpportunityLocationsActions.googleAutocompleteInputChanged,
  NewOpportunityLocationsActions.googleAutocompleteAddressSelected,

  // Track add event
  WorkOrdersActions.eventTypeMenuItemSelected,

  // Track event menu actions
  WorkOrdersActions.moveToTopMenuItemClicked,
  WorkOrdersActions.moveUpMenuItemClicked,
  WorkOrdersActions.moveDownMenuItemClicked,
  WorkOrdersActions.moveToBottomMenuItemClicked,
  WorkOrdersActions.deleteMenuItemClicked,
  WorkOrdersActions.duplicateWorkOrderAdded,

  // Track event input changes
  WorkOrdersActions.rateInputChanged,
  WorkOrdersActions.rateInputKeyDown,
  WorkOrdersActions.quantityInputChanged,

   //Track update charge description
  WorkOrdersActions.chargeDescriptionChanged,

  // Track event notes changes
  WorkOrdersActions.notesChanged,

  // Track add charge
  WorkOrdersActions.chargesAdded,
  WorkOrdersActions.generateChargesWithAISuccess,

  // Track reorder charges
  WorkOrdersActions.chargeDropped,

  // Track remove charge
  WorkOrdersActions.removeChargeMenuItemClicked,
  WorkOrdersActions.removeChargeButtonClicked,

  // Track add preset discount
  WorkOrdersActions.discountAdded,

  // Track remove discount
  WorkOrdersActions.removeDiscountMenuItemClicked,

  // Track summary changes
  JobSummaryActions.summaryEditorContentChanged,

  // Track add room
  InventoryActions.roomAdded,

  // Track change room name
  InventoryActions.editRoomNameInPlaceConfirmButtonClicked,
  JobCreateInventoryActions.roomSelected,

  // Track remove room
  InventoryActions.removeRoomButtonClicked,
  JobCreateInventoryActions.removeRoomButtonClicked,

  // Track add items
  InventoryActions.itemAdded,

  // Track change item quantity
  InventoryActions.itemQuantityInputChanged,
  JobCreateInventoryActions.itemQuantityInputChanged,

  // Track edit item
  InventoryActions.itemEdited,

  // Track remove item
  InventoryActions.removeItemButtonClicked,
  JobCreateInventoryActions.removeItemButtonClicked,

  // Track add comment actions
  JobActivityActions.addCommentContentsChanged,
  JobActivityActions.addCommentModeSet,
  JobActivityActions.addCommentSubjectChanged,

  // Track edit comment
  JobActivityActions.commentEditContentChanged,

  // Track reply contents changed
  JobActivityActions.replyInputContentChanged,

  // Track calculator input changes
  quickJsActions.inputChanged,
  quickJsActions.inputsChanged,
  quickJsActions.inputReset,

  // Track add inventory contents changed
  NewOpportunityAddInventoryActions.addInventoryContentsChanged,

].map(action => action.type);

export const NEW_OPPORTUNITY_ACTION_TYPES = [
  NewOpportunityCustomerActions.fullNameInputChanged,
  NewOpportunityCustomerActions.emailInputChanged,
  NewOpportunityCustomerActions.phoneInputChanged,
  NewOpportunityCustomerActions.companyInputChanged,
  NewOpportunityCustomerActions.clearCustomerButtonClicked,
  NewOpportunityCustomerActions.referralSourceSelected,
  NewOpportunityCustomerActions.customerTypeSelected,
  NewOpportunityCustomerActions.jobOriginSelected,
  NewOpportunityCustomerActions.existingCustomerSelected,
  NewOpportunityCustomerActions.clearCustomerButtonClicked,
  NewOpportunityCustomerActions.googleAutocompleteInputChanged,
  NewOpportunityCustomerActions.googleAutocompleteAddressSelected,
  NewOpportunityLocationsActions.googleAutocompleteInputChanged,
  NewOpportunityLocationsActions.googleAutocompleteAddressSelected,
  OverviewTimelineActions.timelineDatesSelected,
  NewOpportunityCustomerActions.addAdditionalContactButtonClicked,
  NewOpportunityCustomerActions.additionalContactFieldChanged,
  NewOpportunityCustomerActions.removeAdditionalContactButtonClicked,
  LocationDetailsWizardActions.optionClicked,
  JobLocationsOverviewActions.exitWizardButtonClicked,
].map((a) => a.type);

export interface UnsavedChange {
  id: string;
  jobId?: string;
  source: ActionSource;
  action: Action;
}

const unsavedChangesInitialState = {
  queue: compactUnsavedChanges(loadFromStorage('queue', [])),
  replaying: {} as { [ changeId: string ]: boolean },
  saving: {} as { [ changeId: string ]: boolean },
};

export type UnsavedChangesState = typeof unsavedChangesInitialState;

export const unsavedChangesFeature = createFeature({
  name: 'unsavedChanges',
  reducer: createReducer(
    unsavedChangesInitialState,
    on(
      JobToolActions.buildSaveChangesVariablesSuccess,
      JobFormActionButtonActions.buildUpdateJobVariablesSuccess,
      quickJsActions.saveSharedCrewPayStarted,
      (state, { changeIds }): UnsavedChangesState => {

        const saving = { ...state.saving };

        for (const changeId of changeIds) {
          saving[changeId] = true;
        }

        return {
          ...state,
          saving,
        }
      },
    ),
    on(
      JobToolActions.saveChangesSuccess,
      JobActivityActions.createCommentSuccess,
      JobFormActionButtonActions.updateJobSuccess,
      NewOpportunityAddInventoryActions.updateInventorySuccess,
      quickJsActions.saveNavigatorNotesSuccess,
      quickJsActions.saveSharedCrewPaySuccess,
      NavigatorOutboxActions.jobInventoryUpdatedLocally,
      (state, { changeIds }): UnsavedChangesState => {

        const saving = { ...state.saving };

        for (const changeId of changeIds) {
          delete saving[changeId];
        }

        return {
          ...state,
          queue: state.queue.filter((c) => !changeIds.includes(c.id)),
          saving,
        }
      },
    ),
    on(
      JobToolActions.saveChangesError,
      JobFormActionButtonActions.updateJobError,
      NewOpportunityAddInventoryActions.updateInventoryError,
      quickJsActions.saveSharedCrewPayError,
      (state, { changeIds }): UnsavedChangesState => {

        const saving = { ...state.saving };

        for (const changeId of changeIds) {
          delete saving[changeId];
        }

        return {
          ...state,
          saving,
        }
      },
    ),
    on(
      quickJsActions.runCalculationsError,
      (state): UnsavedChangesState => {
        return {
          ...state,
          queue: state.queue.filter((c) => c.source !== ActionSource.QUICK_JS),
        }
      },
    ),
    on(
      JobActivityActions.cancelReplyButtonClicked,
      (state, { commentId }) => ({
          ...state,
          queue: state.queue.filter((c) => {

            if (c.action.type !== JobActivityActions.replyInputContentChanged.type) {
              return true;
            }

            const action = c.action as ReturnType<typeof JobActivityActions.replyInputContentChanged>;

            return action.commentId !== commentId;

          }),
        }),
    ),
    on(
      JobActivityActions.updateCommentSuccess,
      (state, { commentId }) => ({
          ...state,
          queue: state.queue.filter((c) => {

            if (c.action.type !== JobActivityActions.commentEditContentChanged.type) {
              return true;
            }

            const action = c.action as ReturnType<typeof JobActivityActions.replyInputContentChanged>;

            return action.commentId !== commentId;

          }),
        }),
    ),
    on(JobToolActions.jobDataReset, (state, { job }): UnsavedChangesState => {
      return {
        ...state,
        queue: state.queue.filter(
          (c) => c.jobId !== job.id || UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES.includes(c.source),
        ),
      }
    }),
    on(
      NewOpportunityActions.buildCreateJobVariablesSuccess,
      (state): UnsavedChangesState => {

          const saving = { ...state.saving };

          for (const change of state.queue) {
            if (change.source === ActionSource.NEW_OPPORTUNITY_V4) {
              saving[change.id] = true;
            }
          }

          return {
            ...state,
            saving,
          }
        },
    ),
    on(
      NewOpportunityActions.createJobError,
      (state): UnsavedChangesState => {

          const saving = { ...state.saving };

          for (const change of state.queue) {
            if (change.source === ActionSource.NEW_OPPORTUNITY_V4) {
              delete saving[change.id];
            }
          }

          return {
            ...state,
            saving,
          }
        },
    ),
    on(
      NewOpportunityActions.unsavedChangesCleared,
      NewOpportunityActions.createJobSuccess,
      (state): UnsavedChangesState => {
          return {
            ...state,
            queue: state.queue.filter((c) => c.source !== ActionSource.NEW_OPPORTUNITY_V4),
          }
        },
    ),
    on(
      NewOpportunitySimilarJobsActions.discardChangesDialogConfirmButtonClicked,
      (state, { changes }): UnsavedChangesState => {
          return {
            ...state,
            queue: differenceBy(state.queue, changes, 'id'),
          }
        },
    ),
    on(UnsavedChangesActions.replayTriggered, (state, { changes }): UnsavedChangesState => {

      const replaying = { ...state.replaying };

      for (const change of changes) {
        replaying[change.id] = true;
      }

      return {
        ...state,
        replaying,
      }
    }),
    on(
      DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked,
      JobCreateActions.createJobSuccess,
      (state): UnsavedChangesState => {
        return {
          ...state,
          queue: state.queue.filter((c) => c.source !== ActionSource.NEW_OPPORTUNITY),
        }
      }
    ),
  ),
});

export const trackUnsavedChanges =
  (reducer: ActionReducer<any>): ActionReducer<any> =>
(state, action) => {

  let next = reducer(state, action);

  if (
    ('metadata' in action && typeof action.metadata === 'object') &&
    ('changeId' in action.metadata && typeof action.metadata.changeId === 'string') &&
    (action.metadata.changeId in next.unsavedChanges.replaying)
  ) {

    const replaying = { ...next.unsavedChanges.replaying };

    delete replaying[action.metadata.changeId];

    return {
      ...next,
      unsavedChanges: {
        ...next.unsavedChanges,
        replaying,
      },
    };
  }

  let unsavedChange: UnsavedChange;

  if (TRACKABLE_JOB_PAGE_ACTION_TYPES.includes(action.type as any)) {

    const jobId = getRouterParam(next.router.state, 'jobId');

    unsavedChange = {
      id: generateUUID(),
      jobId,
      action,
      source: parseActionSource(action),
    }

  }

  const isNewOpportunity = next.router?.state?.url?.startsWith('/jobs/new');

  if (isNewOpportunity && NEW_OPPORTUNITY_ACTION_TYPES.includes(action.type as any)) {

    unsavedChange = {
      id: generateUUID(),
      action,
      source: ActionSource.NEW_OPPORTUNITY_V4,
    }
  }

  if (!unsavedChange) {
    return next;
  }

  next = {
    ...next,
    unsavedChanges: {
      ...next.unsavedChanges,
      queue: appendUnsavedChange(next.unsavedChanges.queue, unsavedChange),
    },
  };

  return next;
};

export function parseActionSource(action: Action) {

  const open = action.type.indexOf('[');
  if (open === -1) return null;

  const close = action.type.indexOf(']', open + 1);
  if (close === -1) return null;

  return action.type.slice(open + 1, close).trim() as ActionSource;
}

export function getRouterParam(routerState: SerializedRouterStateSnapshot, key: string): string | undefined {

  const stack: any[] = [routerState.root];
  while (stack.length) {
    const node = stack.pop();
    const val = node?.params?.[key];
    if (val != null) return val;

    if (node?.firstChild) stack.push(node.firstChild);
    if (Array.isArray(node?.children)) {
      // Push all children (in case firstChild isn't set or you have aux routes)
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push(node.children[i]);
      }
    }
  }
  return undefined;
}
